// Запуск Mini App (tech/01 §6.1; ux/12 §3): initData из фрагмента → отчёты об ошибках → DOM-экран SCR-BOOT →
// вход и bootstrap параллельно с загрузкой Pixi. ready() — ещё раньше, из сплэша index.html (public/splash.js);
// здесь повторно, для SDK. Полноэкранный режим — ВР-76.
import { render } from 'preact';
import './ui/styles.css';
import { takeInitDataFromLocation } from './boot/init-data.js';
import { createApp, setApp } from './app/app.js';
import { devHooks } from './app/dev-hooks.js';
import { loadStep, makeRelogin, runBoot, type BootCtx } from './boot/boot.js';
import { bindSplash, loadImages } from './boot/splash.js';
import { setAnalyticsTransport, track } from './analytics/analytics.js';
import { createNetworkTransport } from './analytics/transport.js';
import { startAnalyticsSession } from './analytics/session.js';
import { connectWs, emitWs, onWs, wsState } from './net/ws.js';
import { applyTheme } from './telegram/theme.js';
import { applyPrefs } from './state/prefs.js';
import { watchSystemMotion } from './ui/motion.js';
import { applyFps } from './screens/settings.js';
import { initViewport, startFullscreen } from './telegram/viewport.js';
import { bootWait, debugOpen, debugRequested, installDebugGesture } from './ui/debug-panel.js';
import { getWebApp, waitWebApp, type TgWebApp } from './telegram/webapp.js';
import { App, stage, stageReady } from './ui/App.js';
import { reboot } from './screens/system.js';
import { watchRefunds } from './screens/store-purchases.js';
import { clientConfig, online, refreshProfile, tutorialActive, tutorialStep } from './state/game.js';
import { startTutorial } from './tutorial/flow.js';
import { t as tr } from './i18n/t.js';

const initDataFromUrl = takeInitDataFromLocation(); // до любых отчётов об ошибках (Т-К-20)
const useFixtures = import.meta.env.VITE_USE_FIXTURES === 'true';

// Манифест ассетов — сразу, параллельно со входом: иконки не должны мигать запасными символами.
const manifestReady = import('./assets/manifest.js').then(async (m) => { await m.loadManifest(); return m; });

/** Почему WebApp нет: что есть в window.Telegram, что было во фрагменте адреса, как загрузился telegram-web-app.js. */
function bootWaReason(hadInitData: boolean, waitedMs: number): string {
  const tg = (window as { Telegram?: { WebApp?: { initData?: string; version?: string; platform?: string } } }).Telegram;
  const b = (window as { __tgBoot?: { hashKeys: string[]; script: string; scriptMs: number | null } }).__tgBoot;
  let stored: string;
  try { stored = Object.keys(JSON.parse(sessionStorage.getItem('__telegram__initParams') ?? '{}') as object).join(',') || '-'; } catch { stored = 'err'; }
  return [
    `tg=${tg ? 1 : 0} wa=${tg?.WebApp ? 1 : 0} initData=${tg?.WebApp?.initData?.length ?? -1}`,
    `v=${tg?.WebApp?.version ?? '-'} p=${tg?.WebApp?.platform ?? '-'}`,
    `hash=${b?.hashKeys.join(',') || '-'} urlData=${hadInitData ? 1 : 0}`,
    `script=${b?.script ?? '?'}@${b?.scriptMs ?? '-'} waited=${waitedMs}`,
    `stored=${stored}`,
  ].join('; ');
}

async function main() {
  loadStep('entry_js');
  const q = new URLSearchParams(location.search);
  // Внутри Telegram (initData в адресе) — дождаться telegram-web-app.js (до 8 с, сплэш на экране): он грузится async
  // и мог не успеть, а WebApp, которого не было на входе, приложение уже не подхватит (кнопки, тема, вибрация).
  const waitT0 = performance.now();
  let wa: TgWebApp | null = getWebApp() ?? (initDataFromUrl ? await waitWebApp(8000) : null);
  const waitedMs = Math.round(performance.now() - waitT0);
  let fetchImpl: typeof fetch = (...a) => fetch(...a);
  let mockSubscribe: ((l: (ev: unknown) => void) => () => void) | null = null;
  // nostub=1 — без заглушки Telegram: проверка запуска с настоящим telegram-web-app.js (scripts/tg-embed-repro.mjs).
  if (!wa && (import.meta.env.DEV || useFixtures) && q.get('nostub') !== '1') {
    const { installDevStub } = await import('./telegram/dev-stub.js');
    wa = await installDevStub(import.meta.env.VITE_TG_INITDATA_KEY_HEX ?? '00'.repeat(32));
  }
  if (useFixtures) {
    const { createMockBackend } = await import('./dev/mock-backend.js');
    const mock = createMockBackend(q.get('mock') ?? 'returning', { persist: q.get('persist') === '1', ...(q.get('strings') === '1' ? { strings: true } : {}), ...(q.get('build') === 'fast' ? { buildFast: true } : {}) });
    fetchImpl = mock.fetch;
    mockSubscribe = (l) => mock.subscribe(l);
    devHooks.invoicePaid = (orderId) => mock.simulatePaid(orderId, { ws: q.get('ws') !== 'off' });
    (window as unknown as { __mock: unknown }).__mock = mock;
  }

  const app = createApp({ fetch: fetchImpl, wa, ...(import.meta.env.VITE_GLITCHTIP_DSN ? { glitchtipDsn: import.meta.env.VITE_GLITCHTIP_DSN } : {}) });
  setApp(app);
  app.reporter.install();
  (window as unknown as { __app: unknown }).__app = app;

  wa?.ready();
  wa?.expand();
  startFullscreen(wa, clientConfig.value['client.fullscreen_default'] !== false); // ВР-84: сразу после ready()
  try { wa?.disableVerticalSwipes?.(); } catch { /* ниже 7.7 */ }
  applyTheme(wa);
  applyPrefs(); // размер текста и «меньше движения» (ux/11 §2.1)
  watchSystemMotion(() => applyPrefs()); // системная настройка движения сменилась на ходу
  wa?.onEvent?.('themeChanged', () => applyTheme(wa));
  initViewport(wa);
  const fsSync = () => { document.documentElement.dataset.full = wa?.isFullscreen ? '1' : '0'; };
  wa?.onEvent?.('fullscreenChanged', fsSync);
  fsSync();
  track('app_open', { cold: true, launch_source: wa?.initDataUnsafe?.start_param ? 'link' : 'direct', start_param: wa?.initDataUnsafe?.start_param ?? '', viewport_h: Math.round(innerHeight), dpr: devicePixelRatio });
  // Внутри Telegram без WebApp ни кнопок, ни оплаты: причину — в аналитику (client_error kind=boot_wa), без значений.
  bootWait.value = waitedMs;
  if (debugRequested(location.search, (window as { __tgBoot?: Parameters<typeof debugRequested>[1] }).__tgBoot, wa?.initDataUnsafe?.start_param)) debugOpen.value = true;
  installDebugGesture();
  if (!wa || !wa.initData) track('client_error', { kind: 'boot_wa', message: bootWaReason(!!initDataFromUrl, waitedMs).slice(0, 256), screen: 'SCR-BOOT', fingerprint: 'boot_wa', fatal: false });

  render(<App />, document.getElementById('ui')!);
  app.syncButtons();
  // Пачки аналитики — POST /analytics/batch; при уходе в фон — sendBeacon (в режиме фикстур — fetch мок-сервера).
  setAnalyticsTransport(createNetworkTransport(app, useFixtures
    ? (url, json) => { fetchImpl(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: json, keepalive: true }).catch(() => {}); return true; }
    : undefined));
  startAnalyticsSession(app); // session_start/end, screen_view, perf_sample; пачки и beacon — analytics.ts (E18-T02)
  // Сплэш из index.html держится до готового главного: ступень first из манифеста и первый кадр сцены.
  void manifestReady.then(({ assetUrl, bgId }) => {
    const district = assetUrl(bgId('BG-MAP', 1440)); // поля по бокам поля шире 1440 (styles.css)
    if (district) document.documentElement.style.setProperty('--district-bg', `url("${district}")`);
  }).catch(() => {});
  bindSplash(app, manifestReady.then(({ preloadPack, assetUrl, bgId }) => {
    const garage = assetUrl(bgId('BG-GARAGE', innerWidth)); // фон главного — чтобы он не «всплывал» после сплэша
    return loadImages([...preloadPack('first'), ...(garage ? [garage] : [])]);
  }).catch(() => {}));

  const ctx: BootCtx = { initData: initDataFromUrl ?? wa?.initData ?? null, wa, cold: true };
  app.setRelogin(makeRelogin(app, ctx));
  app.session.onLost = (code) => app.nav.showSystem('SCR-BOOT', { code, text: tr('CL-MAIN-01') });
  reboot.run = () => { void runBoot(app, ctx); };

  // WS: события кошелька, оплаты и вытеснения сессии (tech/03 §19).
  if (mockSubscribe) { mockSubscribe((ev) => emitWs(ev)); wsState.value = 'open'; }
  else connectWs({
    token: () => app.session.current()?.accessToken ?? null,
    poll: (since) => app.call('getSync', { query: { since }, noRetry: true }),
    onGap: () => { void refreshProfile((n) => app.call(n)); },
  });

  // ST-OFFLINE (ux/11 §4, E01-T09): браузер сообщил об обрыве — полоса сразу; связь вернулась — проверяем
  // сервер, зелёная полоса 1.5 с и догоняем данные (профиль, кошелёк, шаг обучения).
  addEventListener('offline', () => { online.value = false; });
  addEventListener('online', () => {
    void app.call('getMeta', { noRetry: true }).then(async () => {
      if (!app.session.current()) return;
      await refreshProfile((n) => app.call(n));
    }).catch(() => {});
  });
  // Возврат в приложение (Bot API 8.0 activated, иначе visibilitychange): свежий профиль и шаг обучения.
  // Бой обновляет себя сам (controller.onResume); незаконченное обучение подхватывается с шага сервера.
  let hiddenAt = 0;
  const onActive = async () => {
    if (document.hidden) { hiddenAt = Date.now(); return; }
    if (!app.session.current() || app.nav.state.system || app.nav.top() === 'SCR-FIGHT') return;
    if (hiddenAt && Date.now() - hiddenAt < 1000) return;
    const before = tutorialStep.value;
    const b = await refreshProfile((n) => app.call(n));
    if (b && b.tutorialStep !== before && tutorialActive(b.tutorialStep)) startTutorial(app, b.tutorialStep, b.activeBattle);
  };
  document.addEventListener('visibilitychange', () => { void onActive(); });
  wa?.onEvent?.('activated', () => { void onActive(); });

  onWs((ev) => {
    if (ev.t === 'session.revoked' && ev.d.reason === 'elsewhere') app.nav.openSheet('MOD-SESSION-ELSEWHERE');
  });
  watchRefunds(app); // MOD-REFUND-NOTICE: первым после входа и по событию payment.refunded (ux/08 §11)

  void runBoot(app, ctx);
  // Ассеты конвейера: манифест — после старта, фоном; ступени ux/13 §8.2 — по очереди, не мешая первому экрану.
  void manifestReady.then(async ({ preloadPack }) => {
    setTimeout(() => { preloadPack('stage2'); preloadPack('fight'); }, 1500);
  });
  // Pixi — отдельным чанком: не тормозит первый экран (ADR-0001, Т-К-60).
  void import('./scene/stage.js').then(async ({ createStage }) => {
    const host = document.getElementById('stage')!;
    const s = await createStage(host);
    app.reporter.watchWebgl(s.app.canvas);
    stage.value = s;
    applyFps();
    if (useFixtures) (window as unknown as { __stage: unknown }).__stage = s;
    stageReady.value = true;
    loadStep('atlas_core');
  }).catch((e: unknown) => app.reporter.report('webgl', e, { fatal: true }));
}

void main();
